import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, ArrowRight, Shield, WifiOff, AlertTriangle } from 'lucide-react';
import { authAPI } from '../../services/api';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const LoginPage = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [errorType, setErrorType] = useState(''); // 'network' | 'auth' | 'server' | ''
  const [serverReachable, setServerReachable] = useState(null); // null=checking, true/false
  const [dbConnected, setDbConnected] = useState(null);

  // Pre-flight server connectivity check on mount
  useEffect(() => {
    const checkServer = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/health`, { method: 'GET', signal: AbortSignal.timeout(8000) });
        if (res.ok) {
          const data = await res.json();
          setServerReachable(true);
          setDbConnected(data.status === 'ok');
        } else {
          setServerReachable(false);
        }
      } catch {
        setServerReachable(false);
      }
    };
    checkServer();
  }, []);

  const getErrorMessage = (err) => {
    // No response at all → network / CORS / server unreachable
    if (!err.response) {
      setErrorType('network');
      if (!navigator.onLine) return 'You are offline. Please check your internet connection.';
      return 'Cannot reach the server. This may be a network issue, or the server may be down. Please contact your administrator.';
    }
    const status = err.response.status;
    const detail = err.response.data?.detail;

    if (status === 401) {
      setErrorType('auth');
      return detail || 'Invalid email or password.';
    }
    if (status === 403) {
      setErrorType('auth');
      return detail || 'Your account is inactive. Contact your administrator.';
    }
    if (status === 422) {
      setErrorType('auth');
      return 'Please enter a valid email address and password.';
    }
    if (status === 503) {
      setErrorType('server');
      return detail || 'Database is temporarily unavailable. The server is running but cannot connect to the database. Please contact your administrator.';
    }
    if (status >= 500) {
      setErrorType('server');
      return 'Server error. Please try again in a few minutes or contact support.';
    }
    setErrorType('server');
    return detail || 'An unexpected error occurred. Please try again.';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setErrorType('');
    setLoading(true);

    try {
      const response = await authAPI.login(form.email, form.password);
      localStorage.setItem('prismoline_token', response.access_token);
      localStorage.setItem('prismoline_refresh_token', response.refresh_token);
      localStorage.setItem('prismoline_user', JSON.stringify(response.user));
      navigate('/dashboard');
    } catch (err) {
      console.error('Login error:', err);
      setError(getErrorMessage(err));
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-gray-50">
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-[480px] flex-col justify-between p-10" style={{ background: 'linear-gradient(180deg, #24549c 0%, #1a3d73 100%)' }}>
        <div>
          <div className="flex items-center gap-2 mb-16">
            <img src="/logo_blue.png" alt="Prismoline" style={{ height: '40px', objectFit: 'contain' }} />
          </div>
          <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'DM Sans' }}>Manufacturing ERP</h2>
          <p className="text-white/80 text-sm leading-relaxed mb-10">
            Manage your entire manufacturing operations — from purchase & production to dispatch & reporting — all in one place.
          </p>
          <div className="space-y-4">
            {['Purchase & Import Management', 'Inventory with Batch Tracking', 'Production Planning & BOM', 'Multi-Branch Dispatch', 'AI-Powered Forecasting', 'Comprehensive Sales Reports'].map((item, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                  <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M2 6L5 9L10 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                </div>
                <span className="text-sm text-white/90">{item}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2 text-white/50 text-xs">
          <Shield className="w-4 h-4" />
          <span>Enterprise-grade security | Cloud hosted</span>
        </div>
      </div>

      {/* Right Panel */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <img src="/logo.png" alt="Prismoline" style={{ height: '32px', objectFit: 'contain' }} />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-1" style={{ fontFamily: 'DM Sans' }}>Sign in to your account</h1>
          <p className="text-sm text-gray-500 mb-8">Access your manufacturing dashboard</p>

          {/* Server connectivity warning */}
          {serverReachable === false && !error && (
            <div data-testid="server-unreachable-warning" className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700 flex items-start gap-2">
              <WifiOff className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">Server unreachable</p>
                <p className="text-xs mt-0.5 text-amber-600">The backend server at <code className="bg-amber-100 px-1 rounded text-xs">{API_BASE}</code> is not responding. Login may fail.</p>
              </div>
            </div>
          )}

          {serverReachable === true && dbConnected === false && !error && (
            <div data-testid="db-unreachable-warning" className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">Database not connected</p>
                <p className="text-xs mt-0.5 text-amber-600">The server is running but cannot reach the database. Login will fail. Please contact your administrator.</p>
              </div>
            </div>
          )}

          {error && (
            <div data-testid="login-error-message" className={`mb-4 p-3 rounded-lg text-sm flex items-start gap-2 ${errorType === 'network' ? 'bg-orange-50 border border-orange-200 text-orange-700' : 'bg-red-50 border border-red-200 text-red-600'}`}>
              {errorType === 'network' ? <WifiOff className="w-4 h-4 mt-0.5 flex-shrink-0" /> : <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />}
              <div>
                <p>{error}</p>
                {errorType === 'network' && (
                  <p className="text-xs mt-1 opacity-75">Server: <code className="bg-orange-100 px-1 rounded">{API_BASE}</code></p>
                )}
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Email Address</label>
              <input data-testid="login-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="admin@prismoline.com" className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition-all" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
              <div className="relative">
                <input data-testid="login-password-input" type={showPassword ? 'text' : 'password'} required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="admin123" className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition-all pr-12" />
                <button data-testid="login-password-visibility-button" type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"><span>{showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}</span></button>
              </div>
            </div>
            <button data-testid="login-submit-button" type="submit" disabled={loading} className="w-full py-3 text-sm font-semibold text-white rounded-lg transition-all duration-300 hover:shadow-lg hover:shadow-blue-200 flex items-center justify-center gap-2 disabled:opacity-70" style={{ backgroundColor: '#24549c' }}>
              {loading ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <>Sign In <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>

          {/* Quick access */}
          <div className="mt-8 p-4 bg-gray-50 rounded-xl border border-gray-100">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Quick Login</p>
            <div className="space-y-2 text-xs">
              {[
                { role: 'Admin', email: 'rishabh@prismoline.com', pass: 'prismoline@123' },
                { role: 'Sales Manager', email: 'prabhat@prismoline.com', pass: 'prismoline@123' },
                { role: 'Salesman', email: 'sales@prismoline.com', pass: 'prismoline@123' },
                { role: 'Accounts', email: 'accounts@prismoline.com', pass: 'prismoline@123' },
              ].map((cred, i) => (
                <button key={i} data-testid={`quick-login-${cred.role.toLowerCase().replace(/\s+/g, '-')}`} onClick={() => setForm({ email: cred.email, password: cred.pass })} className="w-full flex items-center justify-between p-2 rounded-lg hover:bg-white hover:shadow-sm transition-all border border-transparent hover:border-gray-200 text-left">
                  <span className="font-medium text-gray-700">{cred.role}</span>
                  <span className="text-gray-400 font-mono">{cred.email}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
